import { useState } from 'react'
import { useTranslation } from 'react-i18next'
import { Popover, PopoverArrow, PopoverContent, PopoverTrigger } from '@/src/renderer/components/ui/popover'
import { WithTooltip } from '@/src/renderer/components/ui/tooltip'
import { PAGE_HELP_STEPS, usePageHelpSeen, type PageHelpId } from '@/src/renderer/lib/intro'
import { IntroTip } from '../IntroTip'
import './PageHelpButton.scss'

/**
 * **Seitenhilfe** (#323, Variante G): ein runder ?-Button neben dem Seitentitel. Erst ein Klick öffnet die
 * Erklärung der Seite als großen Tooltip — nichts springt von selbst auf. Getönt, bis die Seite einmal
 * erklärt wurde (je Gerät); danach erklärt er immer wieder.
 *
 * Steht neben dem `<h1>`, nicht darin: Sein Name gehörte sonst zur Überschrift.
 *
 * @example
 * <div className="page-help__row">
 *   <h1 className="dormant-page__title">Ruhend</h1>
 *   <PageHelpButton page="dormant" />
 * </div>
 */
export function PageHelpButton({ page }: { page: PageHelpId }) {
  const { t } = useTranslation()
  const { seen, loaded, markSeen } = usePageHelpSeen(page)
  const [open, setOpen] = useState(false)

  const handleOpenChange = (next: boolean) => {
    setOpen(next)
    if (next && !seen) markSeen()
  }

  // Bis der Merker gelesen ist, nicht getönt: Sonst blitzte die Tönung bei jedem Seitenaufbau auf.
  const fresh = loaded && !seen

  return (
    <Popover open={open} onOpenChange={handleOpenChange}>
      <WithTooltip content={t('intro.pageHelp.button')} side="bottom">
        <PopoverTrigger asChild>
          <button
            type="button"
            className={`page-help__button${fresh ? ' page-help__button--new' : ''}${open ? ' page-help__button--open' : ''}`}
            aria-label={t('intro.pageHelp.button')}
          >
            ?
          </button>
        </PopoverTrigger>
      </WithTooltip>
      <PopoverContent side="bottom" align="center" sideOffset={10} collisionPadding={12} className="page-help__popover">
        <IntroTip steps={PAGE_HELP_STEPS[page]} heading={t(`intro.pageHelp.heading.${page}`)} onClose={() => setOpen(false)} />
        <PopoverArrow className="intro-tip__arrow" width={16} height={8} />
      </PopoverContent>
    </Popover>
  )
}
